import { HttpEvent, HttpEventType, HttpInterceptorFn, HttpParams, HttpResponse } from "@angular/common/http";
import { inject } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { REQUEST_ERROR_SHOW_TOAST } from "@ramudden/interceptors";
import { ErrorService, ImpersonationService } from "@ramudden/services";
import { tap } from "rxjs/operators";
import { AuthenticationService } from "../services/authentication.service";

function isLocalAsset(url: string): boolean {
    return url.startsWith("assets/") || url.startsWith("/assets/");
}

export const defaultInterceptor: HttpInterceptorFn = (request, next) => {
    const errorService = inject(ErrorService);
    const translateService = inject(TranslateService);
    const impersonationService = inject(ImpersonationService);
    const authenticationService = inject(AuthenticationService);

    const headers: { [key: string]: string } = {};
    headers["Timezone-Offset"] = new Date().getTimezoneOffset().toString();

    const language = translateService.currentLang;
    if (language) {
        headers["Accept-Language"] = language;
    }

    const impersonatedOrganizationId = impersonationService.organization?.id;
    if (impersonatedOrganizationId) {
        headers["Impersonation"] = impersonatedOrganizationId.toString();
    }

    if (impersonationService.role && !request.url.includes("/api/v1/Roles")) {
        headers["ImpersonationRole"] = impersonationService.role;
    }

    // Local assets (translations, config) are versioned by the service worker; a cache-buster would make the URL miss
    // the ngsw manifest and pull the newest file into an old bundle. API calls keep it.
    if (request.method === "GET" && !isLocalAsset(request.url)) {
        let params = new HttpParams({
            fromString: request.params.toString(),
        });

        params = params.append("ts", `${Date.now()}`);

        request = request.clone({
            params: params,
        });
    }

    const showToastOnError = request.context?.get(REQUEST_ERROR_SHOW_TOAST);
    request = request.clone({
        setHeaders: headers,
    });

    const handleError = (response: HttpResponse<any>) => {
        if (response.status !== undefined && (response.status < 200 || response.status >= 300)) {
            if (response.status === 401) {
                authenticationService.login();
            } else if (response.status !== 304 && response.status !== 403) {
                errorService.handleError(response, showToastOnError);
            }
        }

        return response;
    };

    const onSuccess = (event: HttpEvent<unknown>) => {
        if (event.type === HttpEventType.Response) {
            return handleError(event);
        }

        return event;
    };

    return next(request).pipe(tap(onSuccess, handleError));
};
